export interface SchedulingSuggestion {
  // The raw suggestion text the AI produced, e.g. "Follow-up in 2 weeks".
  text: string;
  // A parsed target date (yyyy-MM-dd) when a relative timeframe could be
  // resolved; otherwise undefined so the scheduler falls back to today.
  date?: string;
}

const WORD_NUMBERS: Record<string, number> = {
  a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5,
  six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12,
};

// Best-effort resolution of a relative timeframe ("in 2 weeks", "tomorrow",
// "next month") to a concrete yyyy-MM-dd date. Returns undefined when nothing
// can be confidently parsed so the scheduler simply defaults to today.
export function parseTimeframeToDate(text: string, from: Date = new Date()): string | undefined {
  const lower = text.toLowerCase();
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const toIso = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  if (/\btomorrow\b/.test(lower)) {
    d.setDate(d.getDate() + 1);
    return toIso(d);
  }

  const rel = lower.match(/(?:in|after)\s+(\d+|a|an|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s+(day|week|month|year)s?/);
  if (rel) {
    const n = /^\d+$/.test(rel[1]) ? parseInt(rel[1], 10) : WORD_NUMBERS[rel[1]] ?? 1;
    const unit = rel[2];
    if (unit === 'day') d.setDate(d.getDate() + n);
    else if (unit === 'week') d.setDate(d.getDate() + n * 7);
    else if (unit === 'month') d.setMonth(d.getMonth() + n);
    else if (unit === 'year') d.setFullYear(d.getFullYear() + n);
    return toIso(d);
  }

  const next = lower.match(/\bnext\s+(day|week|month|year)\b/);
  if (next) {
    const unit = next[1];
    if (unit === 'day') d.setDate(d.getDate() + 1);
    else if (unit === 'week') d.setDate(d.getDate() + 7);
    else if (unit === 'month') d.setMonth(d.getMonth() + 1);
    else if (unit === 'year') d.setFullYear(d.getFullYear() + 1);
    return toIso(d);
  }

  return undefined;
}

// Splits an assistant message into renderable segments, isolating any
// "Draft message:" block so we can offer a one-click copy of just the
// patient-facing text the doctor would send. When no marker is present the
// whole content is returned as "before" with a null draft.
export function extractDrafts(content: string): { before: string; draft: string | null } {
  const idx = content.search(/draft message\s*:/i);
  if (idx === -1) return { before: content, draft: null };
  const before = content.slice(0, idx).trim();
  const after = content.slice(idx).replace(/^draft message\s*:/i, '').trim();
  return { before, draft: after || null };
}

// Detects a scheduling suggestion the AI flagged with a "Scheduling
// suggestion:" line and turns it into an actionable suggestion. Returns null
// when the message contains no schedulable recommendation.
export function extractScheduling(content: string): SchedulingSuggestion | null {
  const idx = content.search(/scheduling suggestion\s*:/i);
  if (idx === -1) return null;
  const after = content.slice(idx).replace(/^[\s\S]*?scheduling suggestion\s*:/i, '');
  const line = after.split('\n')[0].trim().replace(/[.\s]+$/, '');
  if (!line) return null;
  return { text: line, date: parseTimeframeToDate(line) };
}
